import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { PriceListEntrySummary } from '@aeris/contracts'
import {
  ConfirmDialog,
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  MoneyInput,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import {
  ApiError,
  fetchPriceTiers,
  removePriceItem,
  resolvePriceFor,
  setPriceItem,
} from '../lib/api.ts'
import {
  OUTCOME_LABELS,
  REASON_LABELS,
  formatQuantity,
  formatUnitPrice,
  parseTypedPrice,
  parseTypedQuantity,
  toWireAmount,
} from '../lib/price-format.ts'

/**
 * Tiered pricing for one product in one list.
 *
 * The wholesale example in Master Specification section 15 is exactly this
 * screen: 1–4 at 92, 5–19 at 87, 20+ at 80. Only the lower bound of each tier
 * is stored, because the upper bound is always the next tier's lower bound and
 * storing a fact twice is how it ends up stored inconsistently.
 *
 * The simulator at the bottom is not a convenience. It shows *which* tier
 * decided a price and why the others did not, which is the difference between
 * a price somebody can defend to a customer and a number they have to argue
 * with. It asks the server, so the answer is the one a sale would get.
 */

/** Uma faixa da tabela, como a consulta a devolve. */
type PriceTierRow = {
  id: string
  version: number
  minQuantity: string
  unitPrice: string
}

export type PriceTiersDialogProps = {
  priceListId: string
  entry: PriceListEntrySummary
  canWrite: boolean
  onClose: () => void
}

export const PriceTiersDialog = ({
  priceListId,
  entry,
  canWrite,
  onClose,
}: PriceTiersDialogProps): ReactNode => {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [minQuantity, setMinQuantity] = useState('')
  const [price, setPrice] = useState('')
  const [simulated, setSimulated] = useState('1')

  const tiers = useQuery({
    queryKey: ['price-tiers', priceListId, entry.variantId],
    queryFn: ({ signal }) =>
      fetchPriceTiers(priceListId, entry.variantId, signal),
  })

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['price-tiers', priceListId, entry.variantId],
    })
    await queryClient.invalidateQueries({ queryKey: ['price-entries'] })
    await queryClient.invalidateQueries({ queryKey: ['price-resolution'] })
  }

  const report = (cause: unknown): void => {
    setError(
      cause instanceof ApiError
        ? cause.message
        : 'Não foi possível salvar. Tente novamente.'
    )
  }

  const addTier = useMutation({
    mutationFn: async () => {
      const quantity = parseTypedQuantity(minQuantity)
      const amount = parseTypedPrice(price)

      if (quantity === null || amount === null) {
        throw new ApiError(422, 'invalid_input', 'Informe quantidade e preço.')
      }

      return await setPriceItem(priceListId, {
        variantId: entry.variantId,
        unitPrice: toWireAmount(amount),
        minQuantity: toWireAmount(quantity),
      })
    },
    onSuccess: async () => {
      setError(null)
      setMinQuantity('')
      setPrice('')
      await invalidate()
    },
    onError: report,
  })

  /*
   * Remover uma faixa é mudar o que o cliente paga (F069).
   *
   * O botão ficava numa linha de tabela e disparava direto. A faixa some, o
   * preço da quantidade dela passa a cair na faixa de baixo, e ninguém no
   * balcão fica sabendo — o próximo pedido simplesmente sai com outro valor.
   */
  const [removendo, setRemovendo] = useState<PriceTierRow | null>(null)

  const remove = useMutation({
    mutationFn: async (tier: { id: string; version: number }) =>
      await removePriceItem(priceListId, tier.id, tier.version),
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: report,
  })

  const quantityForSimulation = parseTypedQuantity(simulated)

  const resolution = useQuery({
    queryKey: [
      'price-resolution',
      priceListId,
      entry.variantId,
      quantityForSimulation === null
        ? null
        : toWireAmount(quantityForSimulation),
    ],
    // Skipped rather than sent as "0" while the field is being typed into: a
    // simulator that answers a question nobody asked is noise.
    enabled: quantityForSimulation !== null,
    queryFn: ({ signal }) =>
      resolvePriceFor(
        {
          variantId: entry.variantId,
          quantity:
            quantityForSimulation === null
              ? '1'
              : toWireAmount(quantityForSimulation),
          priceListId,
        },
        signal
      ),
  })

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={`Faixas de preço — ${entry.productName}`}
        description={`${entry.sku} · quantidades a partir das quais cada preço vale.`}
        className="max-w-3xl"
      >
        <div className="flex flex-col gap-4">
          {error === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
            >
              {error}
            </p>
          )}

          {tiers.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>A partir de</TableHeaderCell>
                    <TableHeaderCell>Preço unitário</TableHeaderCell>
                    {canWrite ? (
                      <TableHeaderCell className="text-right">
                        Ações
                      </TableHeaderCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(tiers.data?.tiers ?? []).map((tier) => (
                    <TableRow key={tier.id}>
                      <TableCell className="font-(--font-mono)">
                        {formatQuantity(tier.minQuantity)} {entry.unit}
                        {tier.minQuantity === '0.000000' ? (
                          <Badge tone="neutral" className="ml-2">
                            Base
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-(--font-mono)">
                        {formatUnitPrice(tier.unitPrice)}
                      </TableCell>
                      {canWrite ? (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setRemovendo(tier)
                            }}
                            leadingIcon={
                              <Trash2
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            }
                          >
                            Remover
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                  {(tiers.data?.tiers ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell className="text-(--text-muted)">
                        Nenhum preço nesta tabela.
                      </TableCell>
                      <TableCell />
                      {canWrite ? <TableCell /> : null}
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {canWrite ? (
            <div className="flex flex-wrap items-end gap-3">
              <Field label="A partir de" hint="0 é o preço base">
                {(field) => (
                  <Input
                    {...field}
                    value={minQuantity}
                    onChange={(event) => {
                      setMinQuantity(event.target.value)
                    }}
                    inputMode="decimal"
                    placeholder="5"
                    className="w-32 font-(--font-mono)"
                  />
                )}
              </Field>

              <Field label="Preço unitário">
                {(field) => (
                  <MoneyInput
                    {...field}
                    value={price}
                    onValueChange={setPrice}
                    className="w-40"
                  />
                )}
              </Field>

              <Button
                variant="secondary"
                size="sm"
                loading={addTier.isPending}
                onClick={() => {
                  addTier.mutate()
                }}
              >
                Adicionar faixa
              </Button>
            </div>
          ) : null}

          <div className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) p-3">
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Simular quantidade">
                {(field) => (
                  <Input
                    {...field}
                    value={simulated}
                    onChange={(event) => {
                      setSimulated(event.target.value)
                    }}
                    inputMode="decimal"
                    className="w-32 font-(--font-mono)"
                  />
                )}
              </Field>
            </div>

            {/* Announced: the answer changes without the layout moving. */}
            <p
              aria-live="polite"
              className="text-(length:--text-sm) text-(--text-primary)"
            >
              {resolution.data === undefined
                ? 'Informe uma quantidade.'
                : resolution.data.applies
                  ? `${formatUnitPrice(resolution.data.unitPrice ?? '0')} — ${
                      resolution.data.basis === 'base-price'
                        ? 'preço base'
                        : `faixa a partir de ${formatQuantity(resolution.data.appliedMinQuantity ?? '0')} ${entry.unit}`
                    }, tabela ${resolution.data.priceList?.name ?? ''}`
                  : REASON_LABELS[
                      resolution.data.reason ?? 'no-applicable-tier'
                    ]}
            </p>

            {resolution.data === undefined ||
            resolution.data.tiers.length === 0 ? null : (
              <ul className="flex flex-col gap-0.5 text-(length:--text-xs) text-(--text-secondary)">
                {resolution.data.tiers.map((tier) => (
                  <li key={tier.itemId} className="font-(--font-mono)">
                    {formatQuantity(tier.minQuantity)} {entry.unit} ·{' '}
                    {formatUnitPrice(tier.unitPrice)} ·{' '}
                    {OUTCOME_LABELS[tier.outcome] ?? tier.outcome}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Fechar
              </Button>
            </DialogClose>
          </DialogFooter>
        </div>
      </DialogContent>

      <ConfirmDialog
        open={removendo !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setRemovendo(null)
        }}
        title="Remover esta faixa de preço?"
        description={
          removendo === null
            ? ''
            : `A partir de ${formatQuantity(removendo.minQuantity)} ${entry.unit} o preço deixa de ser ${formatUnitPrice(removendo.unitPrice)} e passa a valer o da faixa anterior. Vale para o próximo pedido, não para os já emitidos.`
        }
        confirmLabel="Remover a faixa"
        loading={remove.isPending}
        onConfirm={() => {
          const alvo = removendo
          setRemovendo(null)
          if (alvo !== null) remove.mutate(alvo)
        }}
      />
    </Dialog>
  )
}
